import React, { useEffect, useRef, useState } from "react";
import { useStore } from "../../../Store";
import { CancelContinueButtons } from "../../Controls/CancelContinueButtons";
import PageLayout, { PageLayoutWithGetData } from "../../Controls/PageLayout";
import BackLink from "../../Controls/BackLink";
import Section from "../../Controls/Sections";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Center,
  Heading,
  HStack,
  Image,
  List,
  ListItem,
  Text,
} from "@chakra-ui/react";
import SearchBox from "../../Controls/SearchBox";
import { DataHolderBrand } from "../../../Types/AppData";
import NominatedRepresentativeDropdown from "../../NominatedRepresentativeDropdown/NominatedRepresentativeDropdown";

export default function SelectDataHolder() {
  const navigate = useNavigate();

  const {
    data,
    setSelectedDataHolder,
    selectedDataHolder,
    selectedConsent,
    setSelectedConsent,
    setLoading,
  } = useStore();

  const [searchFilter, setSearchFilter] = useState("");
  const handleSelectDataHolder = (selectedDataHolderBrandId: string) => {
    const selectedDataHolder = (data?.dataHolderBrands || []).find(
      (dh: any) => {
        return dh.dataHolderBrandId === selectedDataHolderBrandId;
      }
    );

    setSelectedDataHolder(selectedDataHolder);

    if (!selectedDataHolderBrandId) return;

    selectedConsent.dataHolderBrandId = selectedDataHolderBrandId;
    setSelectedConsent(selectedConsent);
  };

  // Once we've selected a Bank, navigate to it
  useEffect(() => {
    if (!selectedDataHolder || !selectedConsent) return;

    setLoading(true);
    navigate("/DhAuth");
  }, [selectedDataHolder, selectedConsent]);

  const filteredDataHolders = (data?.dataHolderBrands || [])
    .filter((dh: DataHolderBrand) => {
      const lowerCaseSearchFilter = searchFilter.toLowerCase();
      const brandNameLowerCase = dh.brandName.toLowerCase();

      let result = brandNameLowerCase.includes(lowerCaseSearchFilter);
      if (result) return true;

      dh.aliases?.forEach((alias) => {
        if (alias.toLowerCase().includes(lowerCaseSearchFilter)) {
          result = true;
        }
      });

      return result;
    })
    .slice()
    .sort((a: any, b: any) => a.brandName.localeCompare(b.brandName));

  const handleChangeSearch = (term: string) => {
    setSearchFilter(term);
    setSelectedDataHolder(null);
  };

  return (
    <PageLayoutWithGetData loading={false}>
      <BackLink page="/DataRequest" />

      <Heading variant="pageHeading" mb={0}>
        Choose Your Bank
      </Heading>
      <Center mb={2}>
        <NominatedRepresentativeDropdown />
      </Center>
      <Box mx={2} mb={4}>
        <Box>
          <Text mb={3}>Search for your banking provider</Text>
          <SearchBox
            placeholder="Enter your bank name"
            ariaLabel="Enter your bank name."
            value={searchFilter}
            onChange={handleChangeSearch}
          />
        </Box>
        <Box mt={3}>
          <Text mb={2}>Select one bank from the list below</Text>
          <Box>
            <DataHolderList
              dataHolders={filteredDataHolders}
              setSelectedDataHolderBrandId={(id) => {
                handleSelectDataHolder(id);
              }}
              currentSelectedBrandId={selectedDataHolder?.dataHolderBrandId}
            />
          </Box>
        </Box>
      </Box>

      <Section>
        <CancelContinueButtons
          role="footer"
          OnclickContinue={() => navigate("/PreAuthentication")}
          btnContinueText="Continue"
          btnDisableContinue={!Boolean(selectedDataHolder?.dataHolderBrandId)}
          step="SelectDataHolder"
        />
      </Section>
    </PageLayoutWithGetData>
  );
}

interface DataHolderListProps {
  dataHolders: any;
  setSelectedDataHolderBrandId: (id: string) => void;
  currentSelectedBrandId: string;
}

function DataHolderList({
  dataHolders,
  setSelectedDataHolderBrandId,
  currentSelectedBrandId,
}: DataHolderListProps) {
  return (
    <>
      <List>
        {dataHolders?.map((dataHolder: any, index: number, arr: any[]) => {
          const isActive =
            dataHolder?.dataHolderBrandId === currentSelectedBrandId;

          const isLast = arr.length - 1 === index;

          return (
            <ListItem
              key={index}
              boxShadow={isActive ? "0px 0px 6px #0027d6" : undefined}
              border={
                isActive
                  ? "0px 0px 6px #0027d6"
                  : "1px solid rgba(0, 0, 0, 0.125)"
              }
              borderBottom={isActive ? undefined : isLast ? undefined : "none"}
              borderRadius={isActive ? "md" : undefined}
              py={2}
              px={6}
            >
              <HStack h="110px">
                <HStack spacing={5} flex={1}>
                  <Image
                    maxH={"100px"}
                    maxW={"100px"}
                    src={dataHolder?.logoUri}
                    alt={dataHolder?.brandName + " Logo"}
                    fallbackSrc="https://via.placeholder.com/150"
                  />

                  <Text fontWeight="bold">{dataHolder?.brandName}</Text>
                </HStack>

                <Button
                  colorScheme={isActive ? "blue" : undefined}
                  data-testid="selectDataHolder"
                  onClick={() =>
                    setSelectedDataHolderBrandId(dataHolder?.dataHolderBrandId)
                  }
                >
                  {dataHolder?.dataHolderBrandId === currentSelectedBrandId
                    ? "Selected"
                    : "Select"}
                </Button>
              </HStack>
            </ListItem>
          );
        })}
      </List>
    </>
  );
}
